import * as React from 'react';
import { createPortal } from 'react-dom';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

import { type PortalContainer, usePortalContainer } from '../../config-context';

/**
 * Read the current HiBro theme type from the document root's `data-theme`
 * (set by the platform's `applyTheme`, 见 13) and keep it reactive to runtime
 * theme switches — replaces shadcn's default `next-themes` dependency, which
 * this platform does not use.
 */
function useDocumentTheme(): 'light' | 'dark' {
  const read = (): 'light' | 'dark' =>
    typeof document !== 'undefined' &&
    document.documentElement.dataset.theme === 'dark'
      ? 'dark'
      : 'light';
  const [theme, setTheme] = React.useState<'light' | 'dark'>(read);

  React.useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setTheme(read()));
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  return theme;
}

function Toaster({ ...props }: ToasterProps) {
  const theme = useDocumentTheme();
  const portalContainer = resolvePortalNode(usePortalContainer());

  const toaster = (
    <Sonner
      theme={theme}
      className="toaster group"
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
        } as React.CSSProperties
      }
      {...props}
    />
  );
  return portalContainer ? createPortal(toaster, portalContainer) : toaster;
}

function resolvePortalNode(
  container: PortalContainer | undefined,
): HTMLElement | ShadowRoot | undefined {
  if (!container) {
    return undefined;
  }
  return 'current' in container ? (container.current ?? undefined) : container;
}

export { Toaster };
